import { useState, useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react'
import { Button } from '@indipia/ui-core'
import { Z_ONBOARDING } from '../constants/zIndex'

export interface TourStep {
  /** 강조할 대상의 CSS selector. null이면 화면 중앙에 카드만 뜬다(웰컴 스텝 등).
   *  selector가 있는데 대상이 DOM에 없으면(예: 아직 공종이 없어 바가 없음) 자동으로 다음 스텝으로 건너뛴다. */
  selector: string | null
  title: string
  body: string
  /** 이 스텝을 보여주기 직전에 실행 — 드롭다운을 열어야 대상이 보이는 경우 등에 사용 */
  onEnter?: () => void
}

export interface OnboardingTourHandle {
  start: () => void
}

interface Props {
  /** localStorage에 "이미 봤음"을 기록할 키 — 사용자·화면별로 구분되게 지어야 한다 */
  storageKey: string
  steps: TourStep[]
  /** 기본 true — 처음 보는 사용자에게 마운트 후 자동 재생 */
  autoStart?: boolean
}

const CARD_W = 280

function clamp(v: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, v))
}

/**
 * 대상 요소의 실제 화면 위 사각형을, 스크롤 가능한 조상(overflow가 clip되는 컨테이너)과
 * 브라우저 뷰포트로 잘라낸다. 간트차트의 날짜 헤더·마일스톤 행처럼 컨테이너보다 훨씬 넓은
 * 요소는 getBoundingClientRect()가 화면 밖으로 잘린 부분까지 포함해 반환하므로, 그대로 쓰면
 * 스포트라이트가 실제 보이는 영역보다 훨씬 길게 그려진다.
 */
function getVisibleRect(el: HTMLElement) {
  let top = 0
  let left = 0
  let right = window.innerWidth
  let bottom = window.innerHeight
  let node = el.parentElement
  while (node) {
    const cs = getComputedStyle(node)
    if (/(auto|hidden|scroll|clip)/.test(cs.overflowX) || /(auto|hidden|scroll|clip)/.test(cs.overflowY)) {
      const r = node.getBoundingClientRect()
      top = Math.max(top, r.top)
      left = Math.max(left, r.left)
      right = Math.min(right, r.right)
      bottom = Math.min(bottom, r.bottom)
    }
    node = node.parentElement
  }
  const r = el.getBoundingClientRect()
  const t = Math.max(r.top, top)
  const l = Math.max(r.left, left)
  const b = Math.min(r.bottom, bottom)
  const rr = Math.min(r.right, right)
  return { top: t, left: l, width: Math.max(0, rr - l), height: Math.max(0, b - t) }
}

export const OnboardingTour = forwardRef<OnboardingTourHandle, Props>(function OnboardingTour(
  { storageKey, steps, autoStart = true },
  ref,
) {
  const [active, setActive] = useState(false)
  const [idx, setIdx] = useState(0)
  const [rect, setRect] = useState<{ top: number; left: number; width: number; height: number } | null>(null)
  const [cardPos, setCardPos] = useState({ top: 0, left: 0 })
  const cardRef = useRef<HTMLDivElement>(null)

  const close = useCallback(() => {
    try { localStorage.setItem(storageKey, '1') } catch { /* 프라이빗 모드 등 — 무시 */ }
    setActive(false)
  }, [storageKey])

  const measure = useCallback((scrollIntoView = false) => {
    const step = steps[idx]
    if (!step) { close(); return }
    const el = step.selector ? document.querySelector<HTMLElement>(step.selector) : null
    if (step.selector && !el) {
      // 대상이 아직 없으면(예: 공종 없음) 다음 스텝으로 자동 스킵, 마지막이면 종료
      if (idx < steps.length - 1) setIdx(i => i + 1)
      else close()
      return
    }
    if (el) {
      // 간트차트처럼 행이 많아 세로 스크롤이 생기는 화면에서, 대상(예: 마일스톤 행)이 스크롤에
      // 밀려 화면 밖에 있으면 스포트라이트가 빈 자리를 가리키게 된다 — 스텝 진입 시 한 번만
      // 스크롤 컨테이너를 최소한으로 스크롤해 대상을 보이게 한 뒤 측정한다(리사이즈 재측정 시엔
      // 사용자가 읽던 위치를 임의로 옮기지 않도록 스크롤하지 않는다).
      if (scrollIntoView) el.scrollIntoView({ block: 'nearest', inline: 'nearest' })
      const v = getVisibleRect(el)
      const p = 6
      setRect({ top: v.top - p, left: v.left - p, width: v.width + p * 2, height: v.height + p * 2 })
    } else {
      setRect(null)
    }
    const ch = cardRef.current?.offsetHeight ?? 160
    let top: number, left: number
    if (el) {
      const r2 = el.getBoundingClientRect()
      top = r2.bottom + 18
      if (top + ch > window.innerHeight - 12) top = Math.max(12, r2.top - ch - 18)
      left = clamp(r2.left, 12, window.innerWidth - CARD_W - 12)
    } else {
      top = (window.innerHeight - ch) / 2
      left = (window.innerWidth - CARD_W) / 2
    }
    setCardPos({ top, left })
  }, [idx, steps, close])

  const start = useCallback(() => {
    try { localStorage.removeItem(storageKey) } catch { /* 무시 */ }
    setIdx(0)
    setActive(true)
  }, [storageKey])

  useImperativeHandle(ref, () => ({ start }), [start])

  // 스텝 전환 시: onEnter 실행(드롭다운 열기 등) → 리렌더 반영을 기다렸다가 측정
  useEffect(() => {
    if (!active) return
    const step = steps[idx]
    if (!step) return
    step.onEnter?.()
    let raf2 = 0
    const raf1 = requestAnimationFrame(() => { raf2 = requestAnimationFrame(() => measure(true)) })
    return () => { cancelAnimationFrame(raf1); cancelAnimationFrame(raf2) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, idx])

  useEffect(() => {
    if (!active) return
    const onResize = () => measure()
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [active, measure])

  const next = useCallback(() => {
    if (idx < steps.length - 1) setIdx(i => i + 1)
    else close()
  }, [idx, steps.length, close])
  const prev = useCallback(() => { if (idx > 0) setIdx(i => i - 1) }, [idx])

  useEffect(() => {
    if (!active) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
      else if (e.key === 'ArrowRight') next()
      else if (e.key === 'ArrowLeft') prev()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [active, close, next, prev])

  // 처음 방문한 사용자에게 마운트 후 한 번 자동 재생
  useEffect(() => {
    if (!autoStart) return
    let seen = false
    try { seen = !!localStorage.getItem(storageKey) } catch { /* 무시 */ }
    if (!seen) {
      const t = setTimeout(start, 450)
      return () => clearTimeout(t)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!active) return null
  const step = steps[idx]
  if (!step) return null

  return (
    <>
      {!rect && (
        <div style={{ position: 'fixed', inset: 0, zIndex: Z_ONBOARDING, background: 'var(--alpha-black-500)', pointerEvents: 'none' }} />
      )}
      {rect && (
        <div
          style={{
            position: 'fixed', zIndex: Z_ONBOARDING, borderRadius: 12, border: '2px solid var(--gt-primary)',
            boxShadow: '0 0 0 9999px var(--alpha-black-500)', pointerEvents: 'none',
            transition: 'left .25s ease, top .25s ease, width .25s ease, height .25s ease',
            top: rect.top, left: rect.left, width: rect.width, height: rect.height,
          }}
        />
      )}
      <div
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-label={step.title}
        onClick={e => e.stopPropagation()}
        style={{
          position: 'fixed', zIndex: Z_ONBOARDING + 1, top: cardPos.top, left: cardPos.left, width: CARD_W,
          background: 'var(--shell-surface)', borderRadius: 16, boxShadow: 'var(--elevation-3)', padding: 18,
          transition: 'top .25s ease, left .25s ease', fontFamily: 'inherit',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <span style={{ fontSize: 'var(--type-caption-size)', fontWeight: 700, color: 'var(--gt-primary)', letterSpacing: '.3px' }}>{idx + 1} / {steps.length}</span>
          <button
            onClick={close}
            title="가이드 닫기"
            aria-label="가이드 닫기"
            style={{ width: 24, height: 24, border: 'none', background: 'var(--shell-bg)', borderRadius: '50%', color: 'var(--shell-on-surface-2)', cursor: 'pointer', fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'inherit', lineHeight: 1 }}
          >×</button>
        </div>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--shell-on-surface)', marginBottom: 6, letterSpacing: '-.3px' }}>{step.title}</div>
        <div style={{ fontSize: 12, color: 'var(--shell-on-surface-2)', lineHeight: 1.6, letterSpacing: '-.1px', marginBottom: 16 }}>{step.body}</div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Button
            variant="ghost"
            size="sm"
            onClick={prev}
            style={{ visibility: idx === 0 ? 'hidden' : 'visible' }}
          >이전</Button>
          <Button variant="primary" size="sm" onClick={next}>{idx === steps.length - 1 ? '완료' : '다음'}</Button>
        </div>
      </div>
    </>
  )
})
